'use client';

import { createContext, ReactNode, useContext, useMemo } from 'react';

type CompareListing = {
  enabled: boolean;
  /** The listing's row on the page, which its cards report as the block they sit in. */
  componentIndex?: string;
};

/**
 * Whether the listing a card sits in offers comparison. A page mounts one comparison provider, but
 * each card listing has its own switch (an editor's, per listing), so the cards of a listing
 * that switched it off show no compare control even where another listing on the page does.
 */
const CompareListingContext = createContext<CompareListing>({ enabled: true });

export const CompareListingProvider = ({
  enabled,
  componentIndex,
  children,
}: CompareListing & { children: ReactNode }) => {
  const value = useMemo(
    () => ({ enabled, componentIndex }),
    [enabled, componentIndex],
  );
  return (
    <CompareListingContext.Provider value={value}>
      {children}
    </CompareListingContext.Provider>
  );
};

/** Outside any listing (a card placed on its own) comparison is not switched off. */
export const useCompareListingEnabled = () =>
  useContext(CompareListingContext).enabled;

/** The listing's row on the page; `undefined` outside a listing that says. */
export const useCompareListingIndex = () =>
  useContext(CompareListingContext).componentIndex;
